import WebFont from 'webfontloader'
import useMedia from 'use-media'
import { BrowserRouter as Router } from 'react-router-dom'
import {
  Box,
  Columns,
  Col,
  Container,
  mediaQueries,
} from '@redriverpress/system'

import ElliiThemeProvider from 'Common/components/ElliiThemeProvider'
import Breadcrumbs from 'Common/components/Breadcrumbs'
import getInitialState from 'Common/utils/getInitialState'

import {
  AllMaterialsGridHit,
  AllMaterialsListHit,
  AudienceFilter,
  InfiniteHits,
  LevelFilter,
  MaterialTypeFilter,
  Sort,
  ViewModeToggle,
  compactView,
  useViewMode,
} from 'Packages/search'

import ORIGIN from 'Common/constants/search'
import CollectionGridViewWrapper from './collection/CollectionGridViewWrapper'
import CollectionIndex from './collection/CollectionIndex'
import CollectionIndexMenu from './collection/CollectionIndexMenu'
import CollectionHeader from './collection/CollectionHeader'
import MaterialsCount from './collection/MaterialsCount'
import SearchConfig from './collection/SearchConfig'
import CollectionAndSublistTags from './collection/CollectionAndSublistTags'

export default function Collection() {
  const {
    categorySlug,
    categoryTitle,
    hasAudienceFilter,
    hasLevelFilter,
    hasMaterialTypeFilter,
    slug,
    title,
    userIsElliiAdmin,
  } = getInitialState()

  const [viewMode] = useViewMode()

  WebFont.load({
    google: {
      families: ['Open Sans:400,400italic,600,700'],
    },
  })

  const breadcrumbRoutes = [
    {
      label: 'Collections',
      to: '/collections',
      external: true,
    },
    {
      label: categoryTitle,
      to: `/collections/${categorySlug}/`,
      external: true,
    },
    {
      label: title,
      to: `/collections/${categorySlug}/${slug}`,
    },
  ]

  const isLg = useMedia(mediaQueries.lg)

  return (
    <Router basename={`/collections/${categorySlug}/${slug}`}>
      <ElliiThemeProvider>
        <Breadcrumbs routes={breadcrumbRoutes} />
        <CollectionHeader />
        <SearchConfig>
          {!isLg && <CollectionIndexMenu />}
          <Box py={[3, null, null, 4]} bg='gray50'>
            <Container>
              <Columns>
                {isLg && (
                  <Col width={1 / 3}>
                    <CollectionIndex />
                  </Col>
                )}
                <Col width={[1, null, null, 2 / 3]}>
                  {[
                    hasLevelFilter,
                    hasAudienceFilter,
                    hasMaterialTypeFilter,
                  ].includes(true) && (
                    <Box
                      display='grid'
                      gridGap={['0.5rem', null, null, '1rem']}
                      gridAutoFlow={['row', null, null, 'column']}
                      gridAutoColumns='1fr'
                      gridAutoRows='1fr'
                    >
                      {hasLevelFilter && <LevelFilter />}
                      {hasAudienceFilter && <AudienceFilter />}
                      {hasMaterialTypeFilter && <MaterialTypeFilter />}
                    </Box>
                  )}
                  <Box
                    display='flex'
                    alignItems='center'
                    flexWrap='wrap'
                    pt={[2, null, null, 3]}
                    pb={3}
                  >
                    <Box
                      mb={[3, null, null, 0]}
                      order={[1, null, null, 2]}
                      width={['100%', null, null, 'auto']}
                    >
                      <Sort origin={ORIGIN.collections} />
                    </Box>
                    <Box order={[2, null, null, 1]} flexGrow={1}>
                      <MaterialsCount />
                    </Box>
                    <Box order={3} pl={3}>
                      <ViewModeToggle />
                    </Box>
                  </Box>
                  {userIsElliiAdmin && (
                    <Box mb={2}>
                      <CollectionAndSublistTags />
                    </Box>
                  )}
                  <InfiniteHits
                    emptyStateHeading='Sorry!'
                    emptyStateMessage={`You have discovered a content gap on\u00A0Ellii. This list is here as a\u00A0placeholder. Please send us a request if you have a need for content on this topic or\u00A0target. Thank\u00A0you!`}
                    hitComponent={
                      viewMode === compactView
                        ? AllMaterialsGridHit
                        : AllMaterialsListHit
                    }
                    hitsWrapper={
                      viewMode === compactView
                        ? CollectionGridViewWrapper
                        : undefined
                    }
                  />
                </Col>
              </Columns>
            </Container>
          </Box>
        </SearchConfig>
      </ElliiThemeProvider>
    </Router>
  )
}
